import cx from "classnames";
import dayjs from "dayjs";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { REFRESH_BUTTON_CLICKED } from "constants/analyticsEvents";
import { DISPLAY_DATE_HH_MM_A } from "constants/date";
import { RefreshPage } from "constants/refreshPage";
import { useAnalytics } from "hooks/useAnalytics";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { emptyApi } from "store/apis/emptyApi";
import { AppDispatch, RootState } from "store/store";
import RefreshIcon from "static/images/RefreshIcon.svg";

type RefreshButtonProps = {
  endpoints: readonly string[];
  page: RefreshPage;
  shouldShowLoader?: boolean;
};

type CachedQuery = Exclude<
  NonNullable<RootState["api"]["queries"][string]>,
  { status: QueryStatus.uninitialized }
> & { queryCacheKey: string };

const REFETCH_OPTIONS = { subscribe: false, forceRefetch: true } as const;

const selectEndpointQueries = (
  state: RootState,
  endpoints: readonly string[]
): CachedQuery[] =>
  Object.entries(state.api.queries).flatMap(([queryCacheKey, query]) => {
    if (!query || query.status === QueryStatus.uninitialized) return [];
    if (!endpoints.includes(query.endpointName)) return [];

    return [{ ...query, queryCacheKey }];
  });

const selectVisibleQueries = (
  state: RootState,
  endpoints: readonly string[]
): CachedQuery[] =>
  selectEndpointQueries(state, endpoints).filter(
    ({ queryCacheKey }) =>
      Object.keys(state.api.subscriptions[queryCacheKey] ?? {}).length > 0
  );

// Tag invalidation is delayed until every pending request in the app
// settles, so refetch the mounted queries directly instead.
const refetchVisibleQueries =
  (endpoints: readonly string[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    selectVisibleQueries(getState(), endpoints).forEach(
      ({ endpointName, originalArgs }) => {
        // Injected endpoints aren't on emptyApi's static type.
        const endpoint = Reflect.get(emptyApi.endpoints, endpointName);
        void dispatch(endpoint.initiate(originalArgs, REFETCH_OPTIONS));
      }
    );
  };

export const RefreshButton = ({
  endpoints,
  page,
  shouldShowLoader = false,
}: RefreshButtonProps) => {
  const dispatch = useAppDispatch();
  const { trackEvent } = useAnalytics();

  const isRefreshing = useAppSelector((state) =>
    selectEndpointQueries(state, endpoints).some(
      ({ status }) => status === QueryStatus.pending
    )
  );

  const lastUpdatedAt = useAppSelector((state) =>
    Math.max(
      0,
      ...selectVisibleQueries(state, endpoints).map(
        ({ fulfilledTimeStamp }) => fulfilledTimeStamp ?? 0
      )
    )
  );

  const handleRefresh = () => {
    dispatch(refetchVisibleQueries(endpoints));
    trackEvent(REFRESH_BUTTON_CLICKED, { page });
  };

  let tooltipText = "Refresh";

  if (lastUpdatedAt > 0) {
    tooltipText = `Last updated ${dayjs(lastUpdatedAt).format(
      DISPLAY_DATE_HH_MM_A
    )}`;
  }

  return (
    <ToolTip text={tooltipText} side="top" collisionPadding={16}>
      <span>
        <Button
          customType="secondary"
          size="xsmall"
          onClick={handleRefresh}
          disabled={isRefreshing}
          aria-label="Refresh"
        >
          <span
            className={cx("t-flex", {
              "t-animate-spin": shouldShowLoader && isRefreshing,
            })}
          >
            <img src={RefreshIcon} alt="" />
          </span>
        </Button>
      </span>
    </ToolTip>
  );
};
